import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Layers, Code2, Users, Workflow, Megaphone, TrendingUp, Share2 } from "lucide-react";

const ICONS = {
  layers: Layers,
  code: Code2,
  users: Users,
  workflow: Workflow,
  megaphone: Megaphone,
  "trending-up": TrendingUp,
  share: Share2,
};

export default function ServiceCard({ service, index }) {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [8, -8]), { stiffness: 220, damping: 24 });
  const ry = useSpring(useTransform(mx, [0, 1], [-10, 10]), { stiffness: 220, damping: 24 });
  const gx = useTransform(mx, [0, 1], ["0%", "100%"]);
  const gy = useTransform(my, [0, 1], ["0%", "100%"]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  const Icon = ICONS[service.icon] || Layers;

  return (
    <motion.article
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ perspective: 900 }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`service-card-${service.id}`}
      id={service.id}
      className="group relative scroll-mt-28"
    >
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative flex h-full min-h-[320px] flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm transition-all duration-300 hover:border-vibrant-orange/40"
      >
        <motion.div
          style={{
            background: `radial-gradient(400px circle at ${gx.get?.() || "50%"} ${gy.get?.() || "50%"}, rgba(249,115,22,0.18), transparent 55%)`,
          }}
          className="pointer-events-none absolute inset-0"
        />
        <motion.div
          style={{
            background: `radial-gradient(circle at var(--x,50%) var(--y,50%), rgba(249,115,22,0.22), transparent 55%)`,
            "--x": gx,
            "--y": gy,
          }}
          className="pointer-events-none absolute inset-0 opacity-70 mix-blend-screen"
        />

        <div className="relative flex items-start justify-between">
          <span
            className="grid h-14 w-14 place-items-center rounded-2xl border border-vibrant-orange/30 bg-vibrant-orange/10 text-vibrant-orange transition-colors group-hover:bg-vibrant-orange group-hover:text-white"
            style={{ transform: "translateZ(30px)" }}
          >
            <Icon size={24} strokeWidth={2} />
          </span>
          <span
            className="font-heading text-4xl font-extrabold text-white/10 transition-colors group-hover:text-vibrant-orange/40"
            style={{ transform: "translateZ(20px)" }}
          >
            {service.kicker}
          </span>
        </div>

        <h3
          className="mt-6 font-heading text-2xl font-bold tracking-tight text-white"
          style={{ transform: "translateZ(24px)" }}
        >
          {service.title}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-white/60">{service.desc}</p>

        <ul className="mt-5 space-y-1.5 text-xs text-white/50">
          {service.bullets.map((b) => (
            <li key={b} className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-emerald-green" /> {b}
            </li>
          ))}
        </ul>

        <Link
          to={`/services/${service.id}`}
          data-testid={`service-cta-${service.id}`}
          className="mt-6 inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-vibrant-orange transition-colors hover:text-orange-300"
          style={{ transform: "translateZ(20px)" }}
        >
          Learn more
          <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </motion.div>
    </motion.article>
  );
}
